import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { DataTable } from '@/components/common/DataTable';
import {
  AddDeliveryZoneModal,
  EditDeliveryZoneModal,
} from '@/components/features/delivery-zones';
import { DeliveryZoneFilterSidebar, DeliveryZoneFilterValues } from '@/components/features/delivery-zones/DeliveryZoneFilterSidebar';
import { DeliveryZone } from '@/types/delivery-zone.types';
import { MapPin, Plus, Pencil, Trash2, Eye, Loader2, Filter, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useI18n } from '@/contexts/I18nContext';

const initialFilters: DeliveryZoneFilterValues = {
  search: '',
  is_active: '',
};

export default function DeliveryZones() {
  const navigate = useNavigate();
  const { get, delete: deleteZone, loading } = useApi();
  const { toast } = useToast();
  const { tp } = useI18n();

  const [zones, setZones] = useState<DeliveryZone[]>([]);
  const [filters, setFilters] = useState<DeliveryZoneFilterValues>(initialFilters);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingZone, setEditingZone] = useState<DeliveryZone | null>(null);
  const [deletingZone, setDeletingZone] = useState<DeliveryZone | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchZones = async () => {
    try {
      const response: any = await get('/api/v1/settings/delivery-zones');
      setZones(response || []);
    } catch (error) {
      console.error('Failed to fetch delivery zones:', error);
    }
  };

  useEffect(() => {
    fetchZones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteZone = async () => {
    if (!deletingZone) return;
    setDeleteLoading(true);
    try {
      await deleteZone(`/api/v1/settings/delivery-zones/${deletingZone.id}`);
      toast({ title: tp.deliveryZones.deleteSuccessTitle, description: tp.deliveryZones.deleteSuccessDescription });
      fetchZones();
    } catch (error) {
      toast({ title: tp.deliveryZones.errorTitle, description: tp.deliveryZones.deleteErrorDescription, variant: 'destructive' });
    } finally {
      setDeleteLoading(false);
      setDeletingZone(null);
    }
  };

  const handleFilterChange = (key: keyof DeliveryZoneFilterValues, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value === 'all' ? '' : value }));
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
  };

  const filteredZones = zones.filter((z) => {
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (!z.name.toLowerCase().includes(q) && !z.code.toLowerCase().includes(q)) return false;
    }
    if (filters.is_active) {
      const active = filters.is_active === 'true';
      if (z.is_active !== active) return false;
    }
    return true;
  });

  const columns = [
    {
      header: tp.deliveryZones.codeColumnHeader,
      accessor: (row: DeliveryZone) => <span className="font-mono text-sm">{row.code}</span>,
    },
    {
      header: tp.deliveryZones.nameColumnHeader,
      accessor: (row: DeliveryZone) => <span className="font-medium">{row.name}</span>,
    },
    {
      header: tp.deliveryZones.cityColumnHeader,
      accessor: (row: DeliveryZone) => <span>{row.city_name || '-'}</span>,
    },
    {
      header: tp.deliveryZones.descriptionColumnHeader,
      accessor: (row: DeliveryZone) => (
        <span className="text-muted-foreground">{row.description || '-'}</span>
      ),
    },
    {
      header: tp.deliveryZones.statusColumnHeader,
      accessor: (row: DeliveryZone) => (
        <Badge variant={row.is_active ? 'default' : 'secondary'}>
          {row.is_active ? tp.deliveryZones.statusActive : tp.deliveryZones.statusInactive}
        </Badge>
      ),
    },
  ];

  const renderActions = (row: DeliveryZone) => (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        title={tp.deliveryZones.viewAreasTooltip}
        onClick={() => navigate(`/dashboard/settings/delivery-zones/${row.id}`)}
      >
        <Eye className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        title={tp.deliveryZones.editTooltip}
        onClick={() => setEditingZone(row)}
      >
        <Pencil className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-destructive hover:text-destructive"
        title={tp.deliveryZones.deleteTooltip}
        onClick={() => setDeletingZone(row)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </>
  );

  const FilterSidebarContent = (
    <DeliveryZoneFilterSidebar
      filters={filters}
      onFilterChange={handleFilterChange}
      onClearFilters={handleClearFilters}
    />
  );

  return (
    <PageContainer>
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronRight className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">{tp.deliveryZones.pageTitle}</h1>
              <p className="text-sm text-muted-foreground">{tp.deliveryZones.pageDescription}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden">
                  <Filter className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-4">
                <div className="mt-6">{FilterSidebarContent}</div>
              </SheetContent>
            </Sheet>
            <Button onClick={() => setShowAddModal(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              {tp.deliveryZones.addZoneButton}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="flex gap-4 h-[calc(100vh-180px)]">
        <div className="hidden lg:block h-full overflow-y-auto">
          {FilterSidebarContent}
        </div>
        <div className="flex-1 min-w-0">
          {loading ? (
            <LoadingSkeleton type="table" rows={5} />
          ) : filteredZones.length === 0 ? (
            <EmptyState
              icon={<MapPin className="h-16 w-16" />}
              title={tp.deliveryZones.emptyStateTitle}
              description={tp.deliveryZones.emptyStateDescription}
              action={{ label: tp.deliveryZones.addZoneButton, onClick: () => setShowAddModal(true) }}
            />
          ) : (
            <DataTable
              data={filteredZones}
              columns={columns}
              actions={renderActions}
              selectable={true}
              selectedRows={selectedRows}
              onSelectionChange={setSelectedRows}
              emptyMessage={tp.deliveryZones.emptyStateTitle}
            />
          )}
        </div>
      </div>

      <AddDeliveryZoneModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={fetchZones}
      />

      <EditDeliveryZoneModal
        open={!!editingZone}
        zone={editingZone}
        onClose={() => setEditingZone(null)}
        onSuccess={fetchZones}
      />

      <AlertDialog open={!!deletingZone} onOpenChange={() => setDeletingZone(null)}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>{tp.deliveryZones.deleteConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {tp.deliveryZones.deleteConfirmDescription.replace('{name}', deletingZone?.name ?? '')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>{tp.deliveryZones.cancelButton}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteZone}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteLoading}
            >
              {deleteLoading && <Loader2 className="h-4 w-4 ml-2 animate-spin" />}
              {tp.deliveryZones.deleteTooltip}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
